import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import {
  Autocomplete,
  AutocompleteItem,
  Button,
  Checkbox,
  Chip,
  Input,
  Select,
  SelectItem,
  DateField,
} from '@structural-climbing/ui'
import type { GymPlan } from '../plans/types'
import { PlanPriceFields } from '../plans/PlanPriceFields'
import { usePlanDiscount } from '../plans/usePlanDiscount'
import type { DeskPaymentMethod } from '../invoices/paymentMethod'
import { GymDialog } from '../../components/GymDialog'

interface ChildOption {
  id: string
  first_name: string
  last_name: string | null
}

interface BillingForm {
  billingType: 'individual' | 'company'
  firstName: string
  lastName: string
  cnp: string
  companyName: string
  cui: string
  regCom: string
  address: string
  city: string
  county: string
}

const emptyBilling = (): BillingForm => ({
  billingType: 'individual',
  firstName: '',
  lastName: '',
  cnp: '',
  companyName: '',
  cui: '',
  regCom: '',
  address: '',
  city: '',
  county: '',
})

interface BlockingSubscription {
  id: string
  plan_name: string | null
  expires_at: string | null
  entries_remaining: number | null
}

/** The plan a renewal follows. */
export interface RenewTarget {
  subscriptionId: string
  gymPlanId: string
  planName: string
  expiresAt: string | null
  /** The gym's renewal discount, while it applies to this plan. */
  offerPercent: number | null
}

/** The day after a plan's last day, which is when its renewal starts. */
export function renewalStartDate(expiresAt: string | null): Date | null {
  if (!expiresAt) return null
  return new Date(new Date(expiresAt).getTime() + 1)
}

interface AssignPlanModalProps {
  memberEmail: string
  /** The account the gym app is showing. Two accounts can share an email, so
   *  this, not the address, is what the API should key the assignment on. */
  memberUserId?: string | null
  /** Shown when there is no email to show: a member added at the desk. */
  memberName?: string
  childId?: string
  childName?: string
  plans: GymPlan[]
  /** Offer add-on plans only: the member already has a main plan, and this is
   *  for something held next to it, such as a session with an instructor. */
  addonsOnly?: boolean
  /** Sell the member's next plan, to start the day after this one ends. */
  renewOf?: RenewTarget
  onAssigned: () => void
  onClose: () => void
}

export function AssignPlanModal({
  memberEmail,
  memberUserId,
  memberName,
  childId,
  childName,
  plans,
  addonsOnly = false,
  renewOf,
  onAssigned,
  onClose,
}: AssignPlanModalProps) {
  const { getAccessTokenSilently } = useAuth()
  const { t } = useTranslation()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const activePlans = plans.filter(
    (p) => p.is_active && (!addonsOnly || p.is_addon) && (!renewOf || !p.is_addon),
  )

  const today = new Date().toISOString().slice(0, 10)

  const [selectedPlanId, setSelectedPlanId] = useState('')
  const [pricePaid, setPricePaid] = useState('')
  const [isTransfer, setIsTransfer] = useState(false)
  const [transferStartDate, setTransferStartDate] = useState(today)
  const [transferEntriesRemaining, setTransferEntriesRemaining] = useState('')
  const [selectedChildId, setSelectedChildId] = useState(childId ?? '')
  const [children, setChildren] = useState<ChildOption[]>([])
  const [paymentMethod, setPaymentMethod] = useState<DeskPaymentMethod>('cash')
  const [issueInvoice, setIssueInvoice] = useState(false)
  const [billing, setBilling] = useState<BillingForm>(emptyBilling())
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [invoiceFailedSubId, setInvoiceFailedSubId] = useState<string | null>(null)
  const [blocking, setBlocking] = useState<BlockingSubscription | null>(null)

  const selectedPlan = activePlans.find((p) => p.id === selectedPlanId)
  // A partner (ESX, 7card...) pays per visit: no price, no payment, no invoice here.
  const isExternal = !!selectedPlan?.external_provider
  const discount = usePlanDiscount({ selectedPlan, setPricePaid })

  // The renewal discount is for the same plan only, at the gym's percentage.
  // Staff can still change the price; the discount is recorded only when it is
  // the one the gym offers.
  const offer = renewOf?.offerPercent ?? null
  const isSamePlan = !!renewOf && selectedPlanId === renewOf.gymPlanId
  const givesRenewalDiscount =
    offer !== null &&
    isSamePlan &&
    discount.hasDiscount &&
    Number(discount.discountPercent) === offer
  const renewStart = renewalStartDate(renewOf?.expiresAt ?? null)

  // Open a renewal on the plan being renewed, with the discount already applied.
  const prefilled = useRef(false)
  useEffect(() => {
    if (!renewOf || prefilled.current) return
    prefilled.current = true
    const plan = activePlans.find((p) => p.id === renewOf.gymPlanId)
    if (!plan) return
    setSelectedPlanId(plan.id)
    if (offer !== null) discount.apply(plan, offer)
    else setPricePaid(plan.price.toFixed(2))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (childId || renewOf) return
    async function fetchChildren() {
      try {
        const token = await getAccessTokenSilently()
        const res = await fetch(
          `${apiUrl}/members/children?member_email=${encodeURIComponent(memberEmail)}${
            memberUserId ? `&member_user_id=${encodeURIComponent(memberUserId)}` : ''
          }`,
          { headers: { Authorization: `Bearer ${token}` } },
        )
        if (res.ok) {
          const data = (await res.json()) as { children: ChildOption[] }
          setChildren(data.children)
        }
      } catch {
        // silently ignore
      }
    }
    void fetchChildren()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function handlePlanSelect(id: string) {
    setSelectedPlanId(id)
    const plan = activePlans.find((p) => p.id === id)
    if (plan && renewOf && offer !== null) {
      // Back on the same plan, the discount comes back; on another, it goes.
      if (id === renewOf.gymPlanId) discount.apply(plan, offer)
      else {
        discount.reset()
        setPricePaid(plan.price.toFixed(2))
      }
    } else if (plan) setPricePaid(discount.priceFor(plan).toFixed(2))
    setError(null)
  }

  function setBillingField(field: keyof BillingForm, value: string) {
    setBilling((prev) => ({ ...prev, [field]: value }))
  }

  async function handleSubmit() {
    if (!selectedPlanId) return
    setLoading(true)
    setError(null)
    setBlocking(null)
    try {
      const token = await getAccessTokenSilently()

      // One request. The subscription and the order that records the sale are
      // written together server-side — when this was two calls, an abandoned tab
      // between them left the sale missing from every revenue report.
      // A transfer is not a sale, so it carries no payment fields.
      const payload: Record<string, unknown> = {
        member_email: memberEmail,
        ...(memberUserId ? { member_user_id: memberUserId } : {}),
        gym_plan_id: selectedPlanId,
        price_paid: parseFloat(pricePaid) || 0,
        ...(selectedChildId ? { child_id: selectedChildId } : {}),
        ...(renewOf ? { renews_subscription_id: renewOf.subscriptionId } : {}),
        ...(givesRenewalDiscount ? { renewal_discount_percent: offer } : {}),
        ...(isTransfer && transferStartDate ? { starts_at: transferStartDate } : {}),
        ...(isTransfer &&
        selectedPlan?.plan_type === 'entry_based' &&
        transferEntriesRemaining !== ''
          ? { entries_remaining: parseInt(transferEntriesRemaining) }
          : {}),
      }
      if (!isTransfer && !isExternal) {
        payload.payment_method = paymentMethod
        payload.plan_name = selectedPlan?.name ?? ''
        if (issueInvoice && billing.billingType) {
          payload.billing = {
            billing_type: billing.billingType,
            first_name: billing.firstName || undefined,
            last_name: billing.lastName || undefined,
            cnp: billing.cnp || undefined,
            company_name: billing.companyName || undefined,
            cui: billing.cui || undefined,
            reg_com: billing.regCom || undefined,
            address: billing.address || undefined,
            city: billing.city || undefined,
            county: billing.county || undefined,
          }
        }
      }

      const subRes = await fetch(`${apiUrl}/subscriptions`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      const subJson = (await subRes.json()) as {
        error?: string
        id?: string
        order_recorded?: boolean
        blocking_subscription?: BlockingSubscription
      }
      if (!subRes.ok) {
        setError(subJson.error ?? t('assignPlan.errorAssign'))
        setBlocking(subJson.blocking_subscription ?? null)
        return
      }

      // The plan is assigned; only the sale record failed. Offer the retry
      // rather than closing on a number the owner will later find missing.
      if (subJson.order_recorded === false) {
        setInvoiceFailedSubId(subJson.id ?? null)
        setError(t('assignPlan.errorCreateInvoice'))
        return
      }

      onAssigned()
    } catch {
      setError(t('assignPlan.errorAssignRetry'))
    } finally {
      setLoading(false)
    }
  }

  async function handleRetryInvoice() {
    if (!invoiceFailedSubId || !selectedPlan) return
    setLoading(true)
    setError(null)
    try {
      const token = await getAccessTokenSilently()
      const invoiceBody: Record<string, unknown> = {
        subscription_id: invoiceFailedSubId,
        member_email: memberEmail,
        ...(memberUserId ? { member_user_id: memberUserId } : {}),
        payment_method: paymentMethod,
        total_amount: parseFloat(pricePaid) || 0,
        plan_name: selectedPlan.name,
      }
      const invRes = await fetch(`${apiUrl}/invoices`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(invoiceBody),
      })
      if (!invRes.ok) {
        setError(t('assignPlan.errorCreateInvoice'))
        return
      }
      setInvoiceFailedSubId(null)
      onAssigned()
    } catch {
      setError(t('assignPlan.errorAssignRetry'))
    } finally {
      setLoading(false)
    }
  }

  function blockingLabel(sub: BlockingSubscription) {
    const plan = sub.plan_name ?? t('assignPlan.blockedUnknownPlan')
    if (sub.entries_remaining !== null)
      return t('assignPlan.blockedByEntries', { plan, count: sub.entries_remaining })
    if (sub.expires_at)
      return t('assignPlan.blockedByExpiry', {
        plan,
        date: new Date(sub.expires_at).toLocaleDateString(),
      })
    return t('assignPlan.blockedBy', { plan })
  }

  const assignLabel = childName ?? (memberEmail || memberName || '')

  return (
    <GymDialog onClose={onClose} isDismissable={!invoiceFailedSubId}>
      <div className="overflow-y-auto p-6">
        <div className="mb-5">
          <h2 className="text-base font-semibold">
            {renewOf
              ? t('assignPlan.renewTitle')
              : addonsOnly
                ? t('assignPlan.addonTitle')
                : t('assignPlan.title')}
          </h2>
          <p className="text-xs text-foreground/50">
            {t('assignPlan.assigningTo', { name: assignLabel })}
          </p>
        </div>

        {renewOf && (
          <div className="mb-4 flex flex-col gap-2 rounded-lg border border-divider bg-content2/50 px-3 py-2 text-sm">
            <p>
              {renewStart
                ? t('assignPlan.renewStarts', {
                    plan: renewOf.planName,
                    date: renewStart.toLocaleDateString(),
                  })
                : t('assignPlan.renewStartsNoDate', { plan: renewOf.planName })}
            </p>
            {offer !== null && (
              <div className="flex flex-wrap items-center gap-2">
                <Chip size="sm" color="success" variant="flat">
                  {t('assignPlan.renewDiscountChip', { percent: offer })}
                </Chip>
                <span className="text-xs text-foreground/60">
                  {isSamePlan
                    ? t('assignPlan.renewDiscountHint', {
                        date: renewOf.expiresAt
                          ? new Date(renewOf.expiresAt).toLocaleDateString()
                          : '',
                      })
                    : t('assignPlan.renewDiscountSamePlanOnly', { plan: renewOf.planName })}
                </span>
              </div>
            )}
          </div>
        )}

        <div className="flex flex-col gap-4">
          <Autocomplete
            label={t('assignPlan.planLabel')}
            placeholder={t('assignPlan.planPlaceholder')}
            selectedKey={selectedPlanId || null}
            onSelectionChange={(key) => handlePlanSelect(key ? String(key) : '')}
          >
            {activePlans.map((p) => {
              const priceLabel = p.external_provider
                ? t('plans.externalChip', { provider: p.external_provider })
                : `${p.price.toFixed(2)} ${p.currency}`
              const label = `${p.name} — ${priceLabel}${
                p.is_addon && !addonsOnly ? ` · ${t('plans.addonChip')}` : ''
              }`
              return (
                <AutocompleteItem key={p.id} textValue={label}>
                  {label}
                </AutocompleteItem>
              )
            })}
          </Autocomplete>

          {selectedPlan && (
            <>
              {isExternal ? (
                <p className="text-sm text-foreground/60">
                  {t('assignPlan.externalPaidBy', {
                    provider: selectedPlan.external_provider,
                    price: Number(selectedPlan.external_visit_price ?? 0).toFixed(2),
                    currency: selectedPlan.currency,
                  })}
                </p>
              ) : (
                <PlanPriceFields
                  plan={selectedPlan}
                  pricePaid={pricePaid}
                  onPricePaidChange={setPricePaid}
                  discount={discount}
                />
              )}
              {!renewOf && (
                <Checkbox
                  isSelected={isTransfer}
                  onValueChange={(v) => {
                    setIsTransfer(v)
                    if (v && selectedPlan?.plan_type === 'entry_based')
                      setTransferEntriesRemaining(String(selectedPlan.max_entries ?? ''))
                    else setTransferEntriesRemaining('')
                  }}
                >
                  {t('assignPlan.transferToggle')}
                </Checkbox>
              )}
              {isTransfer && (
                <>
                  <DateField
                    label={t('assignPlan.transferStartDateLabel')}
                    value={transferStartDate}
                    onValueChange={setTransferStartDate}
                    description={t('assignPlan.transferStartDateDescription')}
                  />
                  {selectedPlan?.plan_type === 'entry_based' && (
                    <Input
                      label={t('assignPlan.entriesRemainingLabel')}
                      type="number"
                      min="0"
                      value={transferEntriesRemaining}
                      onValueChange={setTransferEntriesRemaining}
                      description={t('assignPlan.transferEntriesRemainingDescription')}
                    />
                  )}
                </>
              )}
            </>
          )}

          {!childId && !renewOf && children.length > 0 && (
            <Select
              label={t('assignPlan.assignToLabel')}
              size="sm"
              placeholder={t('assignPlan.parentDefault')}
              selectedKeys={selectedChildId ? [selectedChildId] : []}
              onSelectionChange={(keys) => {
                const id = [...(keys as Iterable<React.Key>)][0] as string | undefined
                setSelectedChildId(id ?? '')
              }}
            >
              {children.map((c) => (
                <SelectItem
                  key={c.id}
                  textValue={[c.first_name, c.last_name].filter(Boolean).join(' ')}
                >
                  {c.first_name}
                  {c.last_name ? ` ${c.last_name}` : ''}
                </SelectItem>
              ))}
            </Select>
          )}

          {!isTransfer && !isExternal && (
            <Select
              label={t('assignPlan.paymentMethodLabel')}
              selectedKeys={[paymentMethod]}
              onSelectionChange={(keys) => {
                const val = String(Array.from(keys)[0] ?? 'cash') as DeskPaymentMethod
                setPaymentMethod(val)
              }}
            >
              <SelectItem key="cash">{t('assignPlan.cash')}</SelectItem>
              <SelectItem key="card">{t('assignPlan.card')}</SelectItem>
              <SelectItem key="transfer">{t('assignPlan.transfer')}</SelectItem>
            </Select>
          )}

          {!isTransfer && !isExternal && (
            <Checkbox isSelected={issueInvoice} onValueChange={setIssueInvoice}>
              {t('assignPlan.issueInvoice')}
            </Checkbox>
          )}

          {!isTransfer && !isExternal && issueInvoice && (
            <div className="flex flex-col gap-3 rounded-lg border border-divider p-4">
              <Select
                label={t('assignPlan.billingTypeLabel')}
                selectedKeys={[billing.billingType]}
                onSelectionChange={(keys) => {
                  const val = String(Array.from(keys)[0] ?? 'individual') as
                    | 'individual'
                    | 'company'
                  setBilling((prev) => ({ ...prev, billingType: val }))
                }}
              >
                <SelectItem key="individual">{t('assignPlan.individual')}</SelectItem>
                <SelectItem key="company">{t('assignPlan.company')}</SelectItem>
              </Select>

              {billing.billingType === 'individual' ? (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      label={t('assignPlan.firstName')}
                      value={billing.firstName}
                      onValueChange={(v) => setBillingField('firstName', v)}
                    />
                    <Input
                      label={t('assignPlan.lastName')}
                      value={billing.lastName}
                      onValueChange={(v) => setBillingField('lastName', v)}
                    />
                  </div>
                  <Input
                    label={t('assignPlan.cnp')}
                    value={billing.cnp}
                    onValueChange={(v) => setBillingField('cnp', v)}
                  />
                </>
              ) : (
                <>
                  <Input
                    label={t('assignPlan.companyName')}
                    value={billing.companyName}
                    onValueChange={(v) => setBillingField('companyName', v)}
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      label={t('assignPlan.cui')}
                      value={billing.cui}
                      onValueChange={(v) => setBillingField('cui', v)}
                    />
                    <Input
                      label={t('assignPlan.regCom')}
                      value={billing.regCom}
                      onValueChange={(v) => setBillingField('regCom', v)}
                    />
                  </div>
                </>
              )}

              <Input
                label={t('assignPlan.address')}
                value={billing.address}
                onValueChange={(v) => setBillingField('address', v)}
              />
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label={t('assignPlan.city')}
                  value={billing.city}
                  onValueChange={(v) => setBillingField('city', v)}
                />
                <Input
                  label={t('assignPlan.county')}
                  value={billing.county}
                  onValueChange={(v) => setBillingField('county', v)}
                />
              </div>
            </div>
          )}

          {error && (
            <div className="rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">
              <p>{error}</p>
              {blocking && <p className="mt-0.5 text-xs opacity-80">{blockingLabel(blocking)}</p>}
              {invoiceFailedSubId && (
                <p className="mt-0.5 text-xs opacity-80">
                  {t('assignPlan.errorCreateInvoiceHint')}
                </p>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button size="sm" variant="flat" onClick={onClose} isDisabled={loading}>
              {invoiceFailedSubId ? t('common.close') : t('common.cancel')}
            </Button>
            {invoiceFailedSubId ? (
              <Button
                size="sm"
                color="warning"
                isLoading={loading}
                onClick={() => void handleRetryInvoice()}
              >
                {t('assignPlan.retryInvoice')}
              </Button>
            ) : (
              <Button
                size="sm"
                color="primary"
                isLoading={loading}
                isDisabled={!selectedPlanId || loading}
                onClick={() => void handleSubmit()}
              >
                {renewOf ? t('assignPlan.renew') : t('assignPlan.assign')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </GymDialog>
  )
}
